import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Check, X, Sparkles, TrendingUp, HelpCircle, Loader2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Tenant vocabulary admin — house acronyms, value synonyms, and the two
 * learning surfaces (mined suggestions + ambiguity insights). Industry-agnostic.
 */
export function SynonymAdminDialog({ open, onOpenChange }) {
  const [tab, setTab] = useState('acronyms');
  const [acronyms, setAcronyms] = useState([]);
  const [values, setValues] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(false);

  const [aShort, setAShort] = useState('');
  const [aExp, setAExp] = useState('');
  const [vField, setVField] = useState('');
  const [vAlias, setVAlias] = useState('');
  const [vValue, setVValue] = useState('');
  const [models, setModels] = useState([]);
  const [vModel, setVModel] = useState('');
  const [modelFields, setModelFields] = useState([]);
  const [fieldQuery, setFieldQuery] = useState('');
  const [fieldOpen, setFieldOpen] = useState(false);
  const [fieldsLoading, setFieldsLoading] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [a, v, s, i] = await Promise.all([
        api.listAcronyms().catch(() => ({ acronyms: [] })),
        api.listValueSynonyms().catch(() => ({ value_synonyms: [] })),
        api.getSynonymSuggestions().catch(() => ({ suggestions: [] })),
        api.getAmbiguityInsights().catch(() => ({ insights: [] })),
      ]);
      setAcronyms(a.acronyms || []);
      setValues(v.value_synonyms || []);
      setClusters(s.clusters || []);
      setInsights(i.insights || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) loadAll(); }, [open, loadAll]);

  useEffect(() => {
    if (!open) return;
    api.getSemanticModels()
      .then(r => setModels(Array.isArray(r) ? r : (r.models || [])))
      .catch(() => {});
  }, [open]);

  // Load the chosen model's dimension fields for the field picker.
  useEffect(() => {
    if (!vModel) { setModelFields([]); return; }
    setFieldsLoading(true);
    api.getSemanticEntities(vModel)
      .then(r => {
        const ents = Array.isArray(r) ? r : (r.entities || []);
        const fields = [];
        ents.forEach(e => (e.attributes || []).forEach(a => {
          if (a.semantic_type === 'dimension') {
            fields.push({ name: a.name, label: a.business_name || a.display_name || a.name, entity: e.name });
          }
        }));
        fields.sort((x, y) => x.label.localeCompare(y.label));
        setModelFields(fields);
      })
      .catch(() => setModelFields([]))
      .finally(() => setFieldsLoading(false));
    setVField('');
  }, [vModel]);

  const addAcronym = async () => {
    if (!aShort.trim() || !aExp.trim()) return;
    try {
      await api.addAcronym(aShort.trim(), aExp.trim());
      setAShort(''); setAExp('');
      toast.success('Acronym added');
      const a = await api.listAcronyms(); setAcronyms(a.acronyms || []);
    } catch (e) { toast.error(e.message); }
  };
  const delAcronym = async (id) => {
    try {
      await api.deleteAcronym(id);
      setAcronyms(prev => prev.filter(x => x.acronym_id !== id));
    } catch (e) { toast.error(e.message); }
  };

  const addValue = async () => {
    if (!vModel) { toast.error('Pick a model first'); return; }
    if (!vField.trim() || !vAlias.trim() || !vValue.trim()) return;
    try {
      await api.addValueSynonym({ field: vField.trim(), alias: vAlias.trim(), value: vValue.trim(), model_id: vModel });
      setVField(''); setVAlias(''); setVValue('');
      toast.success('Value synonym added');
      const v = await api.listValueSynonyms(); setValues(v.value_synonyms || []);
    } catch (e) { toast.error(e.message); }
  };
  const delValue = async (id) => {
    try {
      await api.deleteValueSynonym(id);
      setValues(prev => prev.filter(x => x.value_synonym_id !== id));
    } catch (e) { toast.error(e.message); }
  };

  const slug = (s) => String(s || '').replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '');

  const approveCluster = async (c) => {
    try {
      await api.approveSynonymSuggestion({ phrase: c.canonical, term: c.canonical, phrases: c.phrases, signal_ids: c.signal_ids });
      setClusters(prev => prev.filter(x => x.canonical !== c.canonical));
      toast.success(c.size > 1 ? `Grouped ${c.size} phrases under "${c.canonical}"` : `Added "${c.canonical}"`);
    } catch (e) { toast.error(e.message); }
  };
  const dismissCluster = async (c) => {
    await Promise.all((c.signal_ids || []).map(id => api.dismissSynonymSuggestion(id).catch(() => {})));
    setClusters(prev => prev.filter(x => x.canonical !== c.canonical));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="synonym-admin-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-cyan-400" /> AI Vocabulary</DialogTitle>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="acronyms" data-testid="tab-acronyms">Acronyms</TabsTrigger>
            <TabsTrigger value="values" data-testid="tab-values">Value Synonyms</TabsTrigger>
            <TabsTrigger value="suggestions" data-testid="tab-suggestions">Suggestions {clusters.length > 0 && <Badge variant="outline" className="ml-1 h-4 px-1 text-[9px]">{clusters.length}</Badge>}</TabsTrigger>
            <TabsTrigger value="ambiguity" data-testid="tab-ambiguity">Ambiguity</TabsTrigger>
          </TabsList>

          {loading && <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>}

          {/* Acronyms */}
          <TabsContent value="acronyms" className="mt-3">
            <p className="text-[11px] text-muted-foreground mb-2">House acronyms expand in AI Explore on top of the industry packs (e.g. "GWP" → gross written premium). Model field synonyms still win.</p>
            <div className="flex gap-2 mb-3">
              <Input value={aShort} onChange={e => setAShort(e.target.value)} placeholder="Short (e.g. GWP)" className="w-32 h-8 text-xs" data-testid="acronym-short-input" />
              <Input value={aExp} onChange={e => setAExp(e.target.value)} placeholder="Expansion (e.g. gross written premium)" className="flex-1 h-8 text-xs" data-testid="acronym-exp-input" onKeyDown={e => e.key === 'Enter' && addAcronym()} />
              <Button size="sm" className="h-8" onClick={addAcronym} data-testid="acronym-add-btn"><Plus className="w-3 h-3" /></Button>
            </div>
            <div className="space-y-1 max-h-64 overflow-auto">
              {acronyms.map(a => (
                <div key={a.acronym_id} className="flex items-center justify-between px-2 py-1.5 rounded-lg bg-muted/20 text-xs" data-testid={`acronym-row-${slug(a.short)}`}>
                  <span><span className="font-mono font-semibold text-cyan-300">{a.short}</span> → {a.expansion}</span>
                  <button onClick={() => delAcronym(a.acronym_id)} className="text-muted-foreground hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
                </div>
              ))}
              {!loading && acronyms.length === 0 && <p className="text-[11px] text-muted-foreground/80 italic py-2">No house acronyms yet.</p>}
            </div>
          </TabsContent>

          {/* Value synonyms (model-scoped) */}
          <TabsContent value="values" className="mt-3">
            <p className="text-[11px] text-muted-foreground mb-2">Map what users type to real dimension values (e.g. "auto" → "Personal Auto"). Pick a model and one of its dimension fields; the mapping applies to that model's query filters automatically.</p>
            <div className="flex gap-2 mb-2">
              <Select value={vModel} onValueChange={setVModel}>
                <SelectTrigger className="w-48 h-8 text-xs" data-testid="value-model-select"><SelectValue placeholder="Select model…" /></SelectTrigger>
                <SelectContent className="max-h-60">{models.map(m => <SelectItem key={m.model_id} value={m.model_id}>{m.name || m.model_id}</SelectItem>)}</SelectContent>
              </Select>
              {/* Searchable dimension-field picker — inline (not a portaled
                  Popover) so it stays inside the modal Dialog's focus scope;
                  a portaled popover here traps focus so search/scroll break. */}
              <div className="relative flex-1" data-testid="value-field-picker">
                <Button variant="outline" disabled={!vModel} onClick={() => setFieldOpen(o => !o)} className="w-full h-8 justify-between text-xs font-normal" data-testid="value-field-select">
                  <span className="truncate">{vField ? (modelFields.find(f => f.name === vField)?.label || vField) : (fieldsLoading ? 'Loading fields…' : 'Select field…')}</span>
                  <ChevronDown className="w-3 h-3 opacity-60 flex-shrink-0" />
                </Button>
                {fieldOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setFieldOpen(false)} />
                    <div className="absolute left-0 top-full mt-1 z-50 w-64 p-2 rounded-md border border-border bg-popover shadow-lg" data-testid="value-field-list">
                      <Input autoFocus value={fieldQuery} onChange={e => setFieldQuery(e.target.value)} placeholder="Search fields…" className="h-7 text-xs mb-2" data-testid="value-field-search" />
                      <div className="max-h-56 overflow-auto space-y-0.5">
                        {modelFields.filter(f => !fieldQuery || f.label.toLowerCase().includes(fieldQuery.toLowerCase()) || f.name.toLowerCase().includes(fieldQuery.toLowerCase()) || (f.entity || '').toLowerCase().includes(fieldQuery.toLowerCase())).slice(0, 100).map(f => (
                          <button key={f.name} onClick={() => { setVField(f.name); setFieldOpen(false); setFieldQuery(''); }} className={`w-full text-left px-2 py-1 rounded text-xs hover:bg-muted/40 ${vField === f.name ? 'bg-primary/10 text-primary' : ''}`} data-testid={`value-field-opt-${slug(f.name)}`}>
                            <span className="block truncate">{f.label}</span>
                            <span className="block truncate text-[9px] text-muted-foreground font-mono">{f.entity}.{f.name}</span>
                          </button>
                        ))}
                        {vModel && fieldsLoading && <p className="text-[10px] text-muted-foreground/80 italic px-2 py-1">Loading fields…</p>}
                        {vModel && !fieldsLoading && modelFields.length === 0 && <p className="text-[10px] text-muted-foreground/80 italic px-2 py-1">No dimension fields.</p>}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="flex gap-2 mb-3">
              <Input value={vAlias} onChange={e => setVAlias(e.target.value)} placeholder="Alias users type (e.g. auto)" className="flex-1 h-8 text-xs" data-testid="value-alias-input" />
              <Input value={vValue} onChange={e => setVValue(e.target.value)} placeholder="Real value (e.g. Personal Auto)" className="flex-1 h-8 text-xs" data-testid="value-value-input" onKeyDown={e => e.key === 'Enter' && addValue()} />
              <Button size="sm" className="h-8" onClick={addValue} disabled={!vModel || !vField} data-testid="value-add-btn"><Plus className="w-3 h-3" /></Button>
            </div>
            <div className="space-y-1 max-h-64 overflow-auto">
              {values.map(v => (
                <div key={v.value_synonym_id} className="flex items-center justify-between px-2 py-1.5 rounded-lg bg-muted/20 text-xs" data-testid={`value-row-${slug(v.alias)}`}>
                  <span><span className="font-mono text-muted-foreground">{v.field}</span>: <span className="text-amber-300">{v.alias}</span> → <span className="text-emerald-300">{v.value}</span></span>
                  <button onClick={() => delValue(v.value_synonym_id)} className="text-muted-foreground hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
                </div>
              ))}
              {!loading && values.length === 0 && <p className="text-[11px] text-muted-foreground/80 italic py-2">No value synonyms yet.</p>}
            </div>
          </TabsContent>

          {/* Learn-from-usage suggestions (auto-grouped) */}
          <TabsContent value="suggestions" className="mt-3">
            <p className="text-[11px] text-muted-foreground mb-2">Terms people ask that the AI didn't recognise — similar phrasings are grouped so you approve one synonym, not five. Approving adds them to your glossary.</p>
            <div className="space-y-1.5 max-h-72 overflow-auto">
              {clusters.map((c) => (
                <div key={c.canonical} className="px-2 py-1.5 rounded-lg bg-muted/20 text-xs" data-testid={`suggestion-cluster-${slug(c.canonical)}`}>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="text-foreground font-medium truncate">{c.canonical}</span>
                      {c.size > 1 && <Badge variant="outline" className="h-4 px-1 text-[9px] border-cyan-500/30 text-cyan-300">{c.size} variants</Badge>}
                      <Badge variant="outline" className="h-4 px-1 text-[9px]">asked {c.total_count}×</Badge>
                    </span>
                    <span className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => approveCluster(c)} className="p-1 rounded hover:bg-emerald-500/20 text-emerald-400" title="Add to glossary" data-testid={`suggestion-approve-${slug(c.canonical)}`}><Check className="w-3.5 h-3.5" /></button>
                      <button onClick={() => dismissCluster(c)} className="p-1 rounded hover:bg-muted/40 text-muted-foreground" title="Dismiss"><X className="w-3.5 h-3.5" /></button>
                    </span>
                  </div>
                  {c.size > 1 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {c.phrases.map(p => <span key={p} className="text-[9px] px-1.5 py-0.5 rounded bg-background/60 text-muted-foreground">{p}</span>)}
                    </div>
                  )}
                </div>
              ))}
              {!loading && clusters.length === 0 && <p className="text-[11px] text-muted-foreground/80 italic py-4 text-center">Nothing to learn yet — suggestions appear as people ask questions.</p>}
            </div>
          </TabsContent>

          {/* Ambiguity insights */}
          <TabsContent value="ambiguity" className="mt-3">
            <p className="text-[11px] text-muted-foreground mb-2">Terms that most often make the AI ask "did you mean…?". Add a clarifying synonym to remove the back-and-forth.</p>
            <div className="space-y-1 max-h-72 overflow-auto">
              {insights.map(s => (
                <div key={s.signal_id} className="flex items-center justify-between px-2 py-1.5 rounded-lg bg-muted/20 text-xs" data-testid={`ambiguity-row-${slug(s.phrase)}`}>
                  <span className="min-w-0"><span className="text-amber-300 font-medium">{s.phrase}</span>{Array.isArray(s.options) && s.options.length > 0 && <span className="text-muted-foreground/70"> — {s.options.join(' / ')}</span>}</span>
                  <Badge variant="outline" className="h-4 px-1 text-[9px] flex items-center gap-0.5"><TrendingUp className="w-2.5 h-2.5" />{s.count}×</Badge>
                </div>
              ))}
              {!loading && insights.length === 0 && <p className="text-[11px] text-muted-foreground/80 italic py-4 text-center flex items-center justify-center gap-1"><HelpCircle className="w-3.5 h-3.5" /> No ambiguous terms yet.</p>}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
